import { LngLatBounds, type GeoJSONSource, type LngLatLike } from 'maplibre-gl'
import circle from '@turf/circle'

import { appState, requestSingleMarker } from '../appstate.svelte'
import { SLIDE_IN_WIDTH } from '$mapapp/config'
import { computePolylineRoute } from '$mapapp/utils/routes_api'
import type { MatchOverviewData } from '../../data/data.model'
import { MATCHING_RADIUS_SRC, toMarkerId } from './model'
import type { Matching } from '../matching.svelte'

/** the secondary slide-in's expanded width, duplicated at `SecondarySlideIn.svelte:71` */
const EXPANDED_SECONDARY_WIDTH_PX = 400

export function drawRouteToPartner(session: Matching, match: MatchOverviewData): void {
	const partnerMarker = appState.allMarkers.get(
		toMarkerId(match.partner.objType, match.partner.objId)
	)

	if (!session.referenceMarker || !partnerMarker) {
		// stash for retry — LayerMatching watches matchMarkers/allMarkers and calls
		// retryPendingRoute() when the partner becomes available.
		session.pendingRouteMatch = match
		/**
		 * the general marker stream is centred on the viewport, so a partner outside it never
		 * arrives on its own and `retryPendingRoute` never gets the chance to fire. such a match
		 * would draw no route at all, which is why its marker is requested by id here.
		 */
		if (partnerMarker === undefined) {
			requestSingleMarker(match.partner.objType, match.partner.objId)
		}
		return
	}

	session.pendingRouteMatch = null

	/**
	 * a partner sitting on the reference coordinates makes a trip of zero length, and the Routes
	 * API answers that with a linestring of a single point. `drawRoute` would push that single
	 * point into the source, which the layer cannot paint, and the previously drawn polyline
	 * would be wiped in the process.
	 */
	if (
		session.referenceMarker.lat === partnerMarker.lat &&
		session.referenceMarker.lon === partnerMarker.lon
	) {
		appState.route.clearCoordinates()
		return
	}

	const origin = {
		lat: session.referenceMarker.lat,
		lon: session.referenceMarker.lon
	} as LngLatLike
	const destination = { lat: partnerMarker.lat, lon: partnerMarker.lon } as LngLatLike

	computePolylineRoute(origin, destination)
		.then(({ polyline }) => {
			/**
			 * a slower fetch for a since-abandoned selection must not overwrite the route for
			 * whatever the user has selected by the time it resolves. re-matching builds a fresh
			 * session (`set_matching` in fsm.svelte.ts) and `destroy()` leaves the discarded
			 * instance's selection and fsm intact, so comparing the id alone cannot detect a
			 * torn-down session.
			 */
			if (
				appState.matching !== session ||
				session.selectedMatchId !== toMarkerId(match.partner.objType, match.partner.objId)
			) {
				return
			}
			if (polyline && session.fsm.current === 'result') {
				appState.route.setCoordinates(polyline)
				session.routingInfoVisible = false
				fitToRouteBounds(polyline)
			}
		})
		.catch((error) => {
			console.error('failed to fetch route for match', error)
		})
}

/**
 * `appState.handleFitBounds` is deliberately not used here: it offsets by `SLIDE_IN_WIDTH` alone,
 * while the route also has to clear the secondary slide-in, whose live width the caller passes.
 */
function fitToRouteBounds(
	polyline: [number, number][],
	secondaryWidthPx: number = EXPANDED_SECONDARY_WIDTH_PX
): void {
	const bounds = new LngLatBounds()
	for (const coordinate of polyline) {
		bounds.extend(coordinate)
	}

	appState.map?.fitBounds(bounds, {
		padding: {
			top: 80,
			bottom: 80,
			left:
				(appState.is_desktop && appState.isSidebarOpened ? SLIDE_IN_WIDTH + secondaryWidthPx : 0) +
				80,
			right: 80
		},
		maxZoom: 14,
		animate: true
	})
}

/**
 * the secondary slide-in collapses to 48px, so its live width comes from the caller. a no-op
 * without a route, which lets a UI handler call it without tracking that precondition.
 */
export function frameRoute(secondaryWidthPx?: number): void {
	const polyline = appState.route.coordinates
	if (!polyline || polyline.length === 0) return

	fitToRouteBounds(polyline, secondaryWidthPx)
}

/**
 * a route fetch deferred because the partner marker had not landed in `appState.allMarkers` yet.
 * a no-op while nothing is pending or the partner is still missing, so callers can fire it
 * repeatedly.
 */
export function redrawPendingRoute(session: Matching): void {
	const pending = session.pendingRouteMatch
	if (!pending) return

	const partnerMarker = appState.allMarkers.get(
		toMarkerId(pending.partner.objType, pending.partner.objId)
	)
	if (!partnerMarker || !session.referenceMarker) return

	drawRouteToPartner(session, pending)
}

/**
 * bounds for the radius annulus come from the reference marker and `confirmedRadius` rather than
 * from the rendered source, because that source is updated through a tween and reading its geometry
 * right after a refetch races the easing. the live source is the fallback for the miniLot case,
 * before the server returns coordinates.
 */
export async function frameRadius(session: Matching): Promise<boolean> {
	const lon = Number(session.referenceMarker?.lon)
	const lat = Number(session.referenceMarker?.lat)
	const radius = session.confirmedRadius
	if (appState.map && Number.isFinite(lon) && Number.isFinite(lat) && radius && radius.to > 0) {
		const polygon = circle([lon, lat], radius.to, { units: 'kilometers', steps: 64 })
		const ring = (polygon.geometry as { coordinates: number[][][] }).coordinates[0]
		const longitudes = ring.map((coordinate) => coordinate[0])
		const latitudes = ring.map((coordinate) => coordinate[1])
		const bounds = new LngLatBounds(
			[Math.min(...longitudes), Math.min(...latitudes)],
			[Math.max(...longitudes), Math.max(...latitudes)]
		)
		appState.handleFitBounds(bounds, { padding: 80, animate: true }, true)
		return true
	}

	const source = appState.map?.getSource(MATCHING_RADIUS_SRC) as GeoJSONSource | undefined
	if (!source) return false
	const bounds = await source.getBounds()
	if (bounds.isEmpty()) return false

	appState.handleFitBounds(bounds, { padding: 80, animate: true }, true)
	return true
}
